import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {FormStatus} from '@iw-shop/forms/components/submit-button/submit-button.component';
import {Subject, takeUntil} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {CustomerService} from '@iw-shop/customer/services/customer.service';
import {CustomerAddress} from '@iw-shop/customer/interfaces/customer';
import {UserService} from '@iw-shop/user/services/user.service';
import {MappedCustomer} from '@store/state/user.state';
import {
    updateMappedCustomer
} from '@store/actions/user.action';
import {
    setSelectedBillingAddress,
    setSelectedDeliveryAddress
} from '@store/actions/storage.action';
import {ApiCustomersUpdateRequest} from '@api/customers';
import {take} from 'rxjs/operators';
import {ApiUsersUpdateRequest} from '@api/users';
import {v4 as uuidv4} from 'uuid';
import {getSelectedCustomer} from '@store/selectors/user.selector';
import {refreshShoppingCartArticles} from '@store/actions/shopping-cart.action';
import {DeliveryAreaService} from "@iw-shop/delivery-area/services/delivery-area.service";
import {ApiError} from "@api/api-error";

@Component({
    selector: 'iw-shop-customer-addresses',
    standalone: false,
    templateUrl: './customer-addresses.component.html',
    styleUrls: ['./customer-addresses.component.scss']
})
export class CustomerAddressesComponent implements OnInit, OnDestroy {

    @Input() selectedAddressId?: string;
    @Input() isAddressOverview?: boolean = false;
    @Input() isSelectAddress?: boolean = false;
    @Input() activeTab?: 'newAddress' | 'updateAddress' | 'addresses' | 'selectAddress';
    @Input() addressType?: 'billing' | 'delivery';

    @Output() onUpdateAddressSuccess: EventEmitter<any> = new EventEmitter();
    @Output() onUpdateDefaultSuccess: EventEmitter<any> = new EventEmitter();
    @Output() onSelectAddress: EventEmitter<any> = new EventEmitter();

    destroy$: Subject<boolean> = new Subject<boolean>();


    selectedCustomer: MappedCustomer | undefined;
    addresses: CustomerAddress[] = [];
    countries?: string[];

    apiError?:ApiError;

    form: FormGroup;

    formStatus: {
        addressId?: string,
        status: FormStatus
    }[] = [];
    submitLabelDefaultAddress: string = '';

    deliveryAreas: { postcode: string, town: string }[] = [];

    constructor(
        protected fb: FormBuilder,
        protected store: Store<AppState>,
        protected deliveryAreaService: DeliveryAreaService,
        protected customerService: CustomerService,
        protected userService: UserService) {
        this.form = this.fb.group({
            addressId: this.fb.control(false),
            default: this.fb.control(false),
            salutation: this.fb.control(''),
            type: this.fb.control(''),
            firstName: this.fb.control(''),
            name: this.fb.control(''),
            company: this.fb.control(''),
            street: this.fb.control(''),
            postcode: this.fb.control(''),
            town: this.fb.control(''),
            phone: this.fb.control(''),
            floor: [0],
            elevator: [null],
            country: this.fb.control('')
        });
        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    if (r.countries) {
                        this.countries = r.countries;
                        if (r.defaultCountry.length > 0) {
                            this.form.get('country')?.setValue(r.defaultCountry);
                        }
                    } else {
                        this.form.get('country')?.setValidators(null);
                        this.form.get('country')?.updateValueAndValidity();
                    }

                }
            );

        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                c => {

                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            let validators: ValidatorFn[] = [];
                            if (r.field == 'country' &&
                                this.countries?.length == 0) {
                                return;
                            }
                            if (r.required) {
                                validators.push(Validators.required);
                            }

                            if (r.regex) {
                                validators.push(Validators.pattern(new RegExp(r.regex)))
                            }
                            if (this.form.get(r.field)) {
                                if (r.disabled) {
                                    this.form.get(r.field)?.disable();
                                }
                                this.form.get(r.field)?.setValidators(validators);
                                this.form.get(r.field)?.updateValueAndValidity();
                            }
                        });
                    }

                }
            );
    }

    ngOnInit(): void {

        this.deliveryAreaService.get({
            deliveryAreas: {
                sort: [{field: 'postcode', direction: 1}],
                limit: 500
            }
        }).subscribe(
            r => {
                if (r.result?.deliveryAreas.data) {
                    this.deliveryAreas = r.result?.deliveryAreas.data;
                }
            }
        );

        this.store.select(getSelectedCustomer)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(
                r => {
                    if (r) {
                        this.selectedCustomer = new MappedCustomer(r);
                        this.addresses = [];
                        r.addresses.map(
                            a => {
                                this.addresses.push(a);
                            }
                        );
                        if (this.addressType !== 'billing') {
                            this.addresses = this.addresses.filter(a => a.type !== 'billing');
                        }
                    }
                }
            );

        switch (this.addressType) {
            case 'billing':
                this.submitLabelDefaultAddress = 'Als Standard-Rechnungsadresse verwenden';
                this.form.get('type')?.setValue('');
                break;

            case 'delivery':
                this.submitLabelDefaultAddress = 'Als Standard-Lieferadresse verwenden';
                this.form.get('type')?.setValue('delivery');
                break;

            default:
                this.submitLabelDefaultAddress = 'Als Standardadresse verwenden';
                this.form.get('type')?.setValue('');
                break;
        }
        if (this.activeTab == 'updateAddress') {
            this.fillFormWithAddress(this.selectedAddressId ?? '');
        } else if (this.activeTab == 'newAddress') {
            this.resetForm();
        }
        this.form.get('type')?.enable();
        if(this.addressType != 'billing'){
            this.form.get('type')?.disable();
        }
        if (!this.hasLoggedInUserAccessToEditAddresses()) {
            this.activeTab = 'selectAddress';
        }
    }

    isDeliveryAddress(): boolean {
        return this.form.get('type')?.value != 'billing';

    }

    hasLoggedInUserAccessToEditAddresses() {
        if(!this.hasAnyRequiredFields()){
            return false;
        }
        return this.userService.hasLoggedInUserAccess({
            type: 'customerAddresses',
            customerId: this.selectedCustomer?.id ?? ''
        });
    }


    resetForm() {
        this.setFormStatus(null);
        this.form.reset();
        this.form.get('type')?.setValue('');
        this.form.get('postcode')?.setValue(''); // Default value after reset
        this.form.get('postcode')?.updateValueAndValidity();
        this.form.get('town')?.setValue(''); // Default value after reset
        this.form.get('town')?.updateValueAndValidity();
        if(this.addressType == 'delivery'){
            this.form.get('type')?.setValue('delivery');
        }
        this.form.get('addressId')?.setValue(uuidv4());
    }

    fillFormWithAddress(addressId: string) {
        this.selectedAddressId = addressId;
        const address = this.getAddress(addressId);
        this.form.get('addressId')?.setValue(address?.addressId);
        this.form.get('type')?.setValue(address?.type);
        this.form.get('salutation')?.setValue(address?.salutation);
        this.form.get('type')?.setValue(address?.type);
        this.form.get('firstName')?.setValue(address?.firstName);
        this.form.get('name')?.setValue(address?.name);
        this.form.get('company')?.setValue(address?.company || '');
        this.form.get('street')?.setValue(address?.street);
        this.form.get('postcode')?.setValue(address?.postcode ?? '');
        this.form.get('town')?.setValue(address?.town ?? '');
        this.form.get('phone')?.setValue(address?.phone);
        this.form.get('elevator')?.setValue(address?.elevator);
        this.form.get('floor')?.setValue(address?.floor);
        this.form.get('country')?.setValue(address?.country);

    }

    isFormRequestError(value: string): boolean {
        let form = this.form?.get(value);
        return !!(form?.errors &&
            this.getFormStatus() == 'submitForm');
    }

    getFieldErrorMsg(field: string, defaultErrorMsg: string): string {
        return this.customerService.getCustomerShopFieldErrorMsg(field, defaultErrorMsg);
    }

    hasUserAccessForCustomerAddress(addressId: string): boolean {
        if (this.selectedCustomer?.guest) {
            return true;
        } else {
            return this.userService.hasUserAccessForCustomerAddress({
                customerId: this.selectedCustomer?.id ?? '',
                addressId
            });
        }

    }

    hasAnyRequiredFields(): boolean {
        return this.customerService.hasAnyRequiredFields();
    }

    isRequired(field: string): boolean {
        return this.customerService.isCustomerShopFieldRequired(field);
    }

    isHiddenOrDisabled(field: string): boolean {
        return (this.customerService.isCustomerShopFieldHidden(field) ||
            this.customerService.isCustomerShopFieldDisabled(field));
    }

    isHidden(field: string): boolean {
        return this.customerService.isCustomerShopFieldHidden(field);
    }

    getAddress(addressId?: string): CustomerAddress | undefined {
        return this.addresses.filter(a => a.addressId == addressId)[0];
    }

    getDeliveryPostcodesGroup(): string[] {
        let postcodes: string[] = [];
        this.deliveryAreas.map(r => {
            if (postcodes.filter(p => p == r.postcode).length == 0) {
                postcodes.push(r.postcode);
            }
        });
        return postcodes;
    }

    getDeliveryTownsByPostcode(postcode: string): string[] {
        return this.deliveryAreas.filter(r => r.postcode == postcode).map(
            r => {
                return r.town;
            }
        );
    }


    setFormStatus(status: FormStatus, addressId?: string) {
        const formStatus = this.formStatus.filter(s => s.addressId == addressId);
        if (formStatus[0]) {
            this.formStatus = this.formStatus.map(s => {
                if (s.addressId == addressId) {
                    return {status, addressId}
                }
                return s;
            });
        } else {
            this.formStatus.push({addressId, status});
        }
    }

    getFormStatus(addressId?: string): FormStatus {
        const status = this.formStatus.filter(s => s.addressId == addressId)[0];
        if (!status) {
            return null;
        }
        return status['status'];
    }

    setDefaultAddress(addressId: string) {

        if (this.selectedCustomer) {
            this.setFormStatus('loading', addressId);
            if (this.addressType == 'billing') {
                this.store.dispatch(setSelectedBillingAddress({customerId: this.selectedCustomer.id, addressId}))
            } else if (this.addressType == 'delivery') {
                this.store.dispatch(setSelectedDeliveryAddress({customerId: this.selectedCustomer.id, addressId}))
            }

            // Kein Gast
            if (!this.selectedCustomer.guest) {
                let userId: string | undefined,
                    shopAccessForCustomers: ApiUsersUpdateRequest['params']['data']['shopAccessForCustomers'] = [];
                this.store.select(state => state)
                    .pipe(
                        take(1)
                    )
                    .subscribe(
                        r => {
                            userId = r.user.id;
                            r.user.shopAccessForCustomers?.map(
                                c => {
                                    if (c.customerId == this.selectedCustomer?.id) {
                                        shopAccessForCustomers?.push({
                                            ...c,
                                            defaultBillingAddressId: r.storage.customer?.selectedBillingAddressId,
                                            defaultDeliveryAddressId: r.storage.customer?.selectedDeliveryAddressId,
                                        });
                                    } else {
                                        shopAccessForCustomers?.push(c);
                                    }

                                }
                            )
                        }
                    );
                if (userId && shopAccessForCustomers) {
                    this.userService.update({id: userId, shopAccessForCustomers})
                        .subscribe(
                            {
                                next: (r) => {
                                    if (r.error) {
                                        this.apiError = r.error;
                                        window.scroll({
                                            top: 0,
                                            left: 0,
                                            behavior: 'smooth'
                                        });
                                        this.setFormStatus('error', addressId);
                                        setTimeout(() => {
                                            this.setFormStatus(null, addressId);
                                        }, 1000);
                                    } else {

                                        this.setFormStatus('success', addressId);
                                        setTimeout(() => {
                                            if (r.result?.users.updated) {
                                                this.setSelectAddress(addressId);
                                            }
                                            this.setFormStatus(null, addressId);
                                        }, 1000);
                                    }
                                },
                                complete: () => {
                                },
                                error: () => {
                                    this.setFormStatus(null, addressId);
                                }

                            }
                        )
                } else {
                    this.setFormStatus(null, addressId);
                }
            }
        }
    }

    setSelectAddress(addressId: string) {
        this.selectedAddressId = addressId;
        if (this.addressType == 'billing') {
            this.store.dispatch(setSelectedBillingAddress({customerId: this.selectedCustomer?.id ?? '', addressId}));
        } else if (this.addressType == 'delivery') {
            this.store.dispatch(setSelectedDeliveryAddress({customerId: this.selectedCustomer?.id ?? '', addressId}));
        }
        this.store.dispatch(refreshShoppingCartArticles({jsonId: 'customerAddresses.setSelectAddress'}));
        this.onSelectAddress.emit();
    }

    setUpdateSuccess() {
        this.onUpdateAddressSuccess.emit();
    }

    deleteAddress(addressId: string) {


        if (confirm('Möchten Sie die Adresse wirklich löschen')) {
            if (this.selectedCustomer) {
                this.addresses = this.addresses.filter(a => a.addressId !== addressId);
                let customer: ApiCustomersUpdateRequest['params']['data'] = {
                    id: this.selectedCustomer.id,
                    addresses: this.addresses
                }
                this.customerService.update(customer)
                    .subscribe(
                        {
                            next: (r) => {
                                this.setFormStatus('submitForm');
                                if (r.error) {
                                    this.apiError = r.error;
                                    window.scroll({
                                        top: 0,
                                        left: 0,
                                        behavior: 'smooth'
                                    });
                                } else {
                                    if (this.selectedCustomer) {
                                        this.store.dispatch(updateMappedCustomer({
                                            customerId: customer.id,
                                            mappedCustomer: {
                                                ...this.selectedCustomer,
                                                addresses: this.addresses
                                            }
                                        }));
                                        /*    this.store.dispatch(setSelectedBillingAddress({
                                              customerId: this.selectedCustomer?.id ?? '',
                                              addressId: (this.selectedCustomer.selectedBillingAddressId != addressId) ? this.selectedCustomer.selectedBillingAddressId ?? '' : ''
                                            }));
                                            this.store.dispatch(setSelectedDeliveryAddress({
                                              customerId: this.selectedCustomer?.id ?? '',
                                              addressId: (this.selectedCustomer.selectedDeliveryAddressId != addressId) ? this.selectedCustomer.selectedDeliveryAddressId ?? '' : ''
                                            }));*/

                                    }
                                }
                            },
                            complete: () => {
                            },
                            error: () => {
                                this.setFormStatus('submitForm');
                            }
                        }
                    )
            }
        }
    }

    updateAddresses() {
        this.setFormStatus('submitForm');

        if (this.form.valid) {
            this.setFormStatus('loading');
            const addressForm: CustomerAddress = {
                addressId: this.form.get('addressId')?.value,
                type: this.form.get('type')?.value,
                salutation: this.form.get('salutation')?.value?.trim() ?? '',
                company: this.form.get('company')?.value?.trim() ?? '',
                firstName: this.form.get('firstName')?.value?.trim() ?? '',
                name: this.form.get('name')?.value?.trim() ?? '',
                street: this.form.get('street')?.value?.trim() ?? '',
                postcode: this.form.get('postcode')?.value?.trim() ?? '',
                town: this.form.get('town')?.value?.trim() ?? '',
                phone: this.form.get('phone')?.value?.trim() ?? '',
                country: this.form.get('country')?.value?.trim() ?? '',
            };
            if (!this.isHiddenOrDisabled('floor')) {
                addressForm.floor = this.form.get('floor')?.value ?? 0;
            }
            if (!this.isHiddenOrDisabled('elevator')) {
                addressForm.elevator = (this.form.get('elevator')?.value == 'true' || this.form.get('elevator')?.value == true);
            }
            if (this.form.get('company')?.value?.trim()) {
                addressForm.gesoftName1 = this.form.get('company')?.value?.trim();
                addressForm.gesoftName2 = this.form.get('firstName')?.value?.trim() + ' ' + this.form.get('name')?.value?.trim();
                if (addressForm.salutation != '') {
                    addressForm.gesoftName2 = this.form.get('salutation')?.value?.trim() + ' '  + addressForm.gesoftName2;
                }
            } else {
                addressForm.gesoftName1 = this.form.get('firstName')?.value?.trim() + ' ' + this.form.get('name')?.value?.trim();
                if (addressForm.salutation != '') {
                    addressForm.gesoftName1 = this.form.get('salutation')?.value?.trim() + ' '  + addressForm.gesoftName1;
                }
                addressForm.gesoftName2 = '';
            }
            addressForm.gesoftName1 = (addressForm.gesoftName1?.length ?? 0) > 100 ? addressForm.gesoftName1?.substring(0, 100) : addressForm.gesoftName1 ?? '';
            addressForm.gesoftName2 = (addressForm.gesoftName2?.length ?? 0) > 100 ? addressForm.gesoftName2?.substring(0, 100) : addressForm.gesoftName2 ?? '';

            if(!this.selectedCustomer){
                console.log('no selected customer');
                return;
            }

            this.addresses = this.selectedCustomer?.addresses.map(a => {
                a = {
                    additionalAddressInformation: a.additionalAddressInformation,
                    addressId: a.addressId,
                    company: a.company,
                    country: a.country,
                    elevator: a.elevator,
                    firstName: a.firstName,
                    floor: a.floor,
                    gesoftName1: a.gesoftName1,
                    gesoftName2: a.gesoftName2,
                    mobile: a.mobile,
                    name: a.name,
                    phone: a.phone,
                    postcode: a.postcode,
                    salutation: a.salutation,
                    street: a.street,
                    title: a.title,
                    town: a.town,
                    type: a.type
                }
                if (a.addressId == addressForm.addressId) {
                    a = {...a, ...addressForm};
                }
                return a;
            })
            // new address
            if (!this.selectedCustomer.addresses.filter(a => a.addressId == addressForm.addressId)[0]) {
                this.addresses.push(addressForm);
            }

            if (this.selectedCustomer) {
                let mappedCustomer: MappedCustomer = {
                    ...this.selectedCustomer,
                    addresses: this.addresses
                }

                //  guest
                if (this.selectedCustomer?.guest) {
                    this.store.dispatch(updateMappedCustomer({
                        customerId: this.selectedCustomer.id,
                        mappedCustomer: mappedCustomer
                    }));
                    this.setSelectAddress(addressForm.addressId ?? '');
                    this.setFormStatus(null);
                    this.setUpdateSuccess();
                } else if (this.selectedCustomer?.id) {
                    let customer: ApiCustomersUpdateRequest['params']['data'] = {
                        id: this.selectedCustomer.id,
                        addresses: this.addresses
                    }
                    customer.status = 'changed';
                    this.customerService.update(customer)
                        .subscribe(
                            {
                                next: (r) => {
                                    this.setFormStatus('submitForm');
                                    if (r.error) {
                                        this.apiError = r.error;
                                        window.scroll({
                                            top: 0,
                                            left: 0,
                                            behavior: 'smooth'
                                        });
                                    } else {
                                        this.setFormStatus('success');
                                        setTimeout(() => {
                                            if (r.result?.customers.updated) {
                                                this.store.dispatch(updateMappedCustomer({
                                                    customerId: customer.id,
                                                    mappedCustomer: mappedCustomer
                                                }));
                                                this.setSelectAddress(addressForm.addressId ?? '');
                                                this.setUpdateSuccess();
                                            }
                                            this.setFormStatus(null);
                                        }, 1000);


                                    }
                                },
                                complete: () => {
                                },
                                error: () => {
                                    this.setFormStatus('submitForm');
                                }
                            }
                        )
                }
            }
        } else {
            this.setFormStatus('submitForm');
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
